import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Building2, 
  Shirt, 
  Loader2, 
  Search, 
  Plus, 
  Trash2,
  Package
} from 'lucide-react';
import { fetchInventarioVerticalByEdificio } from '@/services/inventarioLenceriaVertical.service';

const CrearEntregaDirectaModal = ({ isOpen, onClose, onSuccess, edificio = null }) => {
  const { toast } = useToast();
  const [edificios, setEdificios] = useState([]);
  const [edificioSeleccionado, setEdificioSeleccionado] = useState(null);
  const [prendasDisponibles, setPrendasDisponibles] = useState([]);
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [searchEdificio, setSearchEdificio] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEdificios, setIsLoadingEdificios] = useState(false);
  const [isLoadingPrendas, setIsLoadingPrendas] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  // Cargar edificios con logística vertical
  useEffect(() => {
    if (isOpen) {
      loadEdificios();
      // Si se pasa un edificio como parámetro, preseleccionarlo
      if (edificio) {
        setEdificioSeleccionado(edificio);
      }
    } else {
      // Limpiar estado cuando se cierra el modal
      setEdificioSeleccionado(null);
      setPrendasDisponibles([]);
      setItemsSeleccionados([]);
      setObservaciones('');
      setSearchEdificio('');
    }
  }, [isOpen, edificio]);

  // Cargar prendas cuando se selecciona un edificio
  useEffect(() => {
    if (edificioSeleccionado) {
      loadPrendasParametrizadas();
    }
  }, [edificioSeleccionado]);

  const loadEdificios = async () => {
    setIsLoadingEdificios(true);
    try {
      const response = await fetch(`${API_URL}/edificios`);
      if (!response.ok) throw new Error('Error al cargar edificios');
      
      const data = await response.json();
      // Filtrar solo edificios con logística vertical
      const edificiosVerticales = data.filter(
        edificio => edificio.tipoLogistica === 'vertical'
      );
      setEdificios(edificiosVerticales);
    } catch (error) {
      console.error('Error al cargar edificios:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los edificios',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingEdificios(false);
    }
  };

  const loadPrendasParametrizadas = async () => {
    if (!edificioSeleccionado?._id) {
      setPrendasDisponibles([]);
      return;
    }

    setIsLoadingPrendas(true);
    try {
      // Obtener inventario vertical del edificio seleccionado
      const response = await fetchInventarioVerticalByEdificio(edificioSeleccionado._id);
      
      if (!response || !response.data) {
        setPrendasDisponibles([]);
        toast({
          title: 'Información',
          description: 'Este edificio no tiene inventario disponible',
        });
        return;
      }

      const inventarioItems = response.data;

      if (inventarioItems.length === 0) {
        setPrendasDisponibles([]);
        toast({
          title: 'Información',
          description: 'Este edificio no tiene prendas en inventario',
        });
        return;
      }

      // Mapear inventario a formato de prendas disponibles
      const prendasConStock = inventarioItems.map(item => ({
        _id: item.tipo_prenda?.id || item._id,
        nombre: item.tipo_prenda?.nombre || 'Sin nombre',
        peso: item.tipo_prenda?.peso || 0,
        stock_disponible: item.cantidad_disponible || 0,
        inventario_id: item._id,
        estado: item.estado
      }));

      // Mostrar todas las prendas, sin importar el stock
      if (prendasConStock.length === 0) {
        toast({
          title: 'Información',
          description: 'No hay prendas parametrizadas en este edificio',
        });
      }

      setPrendasDisponibles(prendasConStock);
    } catch (error) {
      console.error('Error al cargar inventario vertical:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el inventario del edificio',
        variant: 'destructive'
      });
      setPrendasDisponibles([]);
    } finally {
      setIsLoadingPrendas(false);
    }
  };

  const handleCantidadChange = (prendaId, nuevaCantidad) => {
    const cantidad = parseInt(nuevaCantidad) || 0;
    
    if (cantidad === 0) {
      // Si la cantidad es 0, eliminar el item
      setItemsSeleccionados(itemsSeleccionados.filter(item => item.prenda_id !== prendaId));
      return;
    }

    const prenda = prendasDisponibles.find(p => p._id === prendaId);
    if (!prenda) return;

    // Verificar si ya existe
    const itemExistente = itemsSeleccionados.find(item => item.prenda_id === prendaId);
    
    if (itemExistente) {
      // Actualizar cantidad
      setItemsSeleccionados(itemsSeleccionados.map(item => 
        item.prenda_id === prendaId 
          ? { ...item, cantidad: cantidad }
          : item
      ));
    } else {
      // Agregar nuevo item
      const nuevoItem = {
        id: `temp-${Date.now()}`,
        prenda_id: prenda._id,
        nombre: prenda.nombre,
        peso: prenda.peso || 0,
        cantidad: cantidad,
        stock_disponible: prenda.stock_disponible,
        tipo: 'directo'
      };
      setItemsSeleccionados([...itemsSeleccionados, nuevoItem]);
    }
  };

  const getCantidadActual = (prendaId) => {
    const item = itemsSeleccionados.find(item => item.prenda_id === prendaId);
    return item ? item.cantidad : '';
  };

  const handleCrearEntrega = async () => {
    // Validaciones
    if (!edificioSeleccionado) {
      toast({
        title: 'Error',
        description: 'Debe seleccionar un edificio',
        variant: 'destructive'
      });
      return;
    }

    if (itemsSeleccionados.length === 0) {
      toast({
        title: 'Error',
        description: 'Debe agregar al menos una prenda',
        variant: 'destructive'
      });
      return;
    }

    const itemsConCantidad = itemsSeleccionados.filter(item => item.cantidad > 0);
    if (itemsConCantidad.length === 0) {
      toast({
        title: 'Error',
        description: 'Debe especificar cantidades mayores a 0',
        variant: 'destructive'
      });
      return;
    }

    // Validar stock
/*     const itemsSinStock = itemsConCantidad.filter(item => item.cantidad > item.stock_disponible);
    if (itemsSinStock.length > 0) {
      toast({
        title: 'Error',
        description: `Stock insuficiente para: ${itemsSinStock.map(i => i.nombre).join(', ')}`,
        variant: 'destructive'
      });
      return;
    } */

    setIsLoading(true);
    try {
      const usuario = {
        id: localStorage.getItem('userId') || '68ee96b27238402c10e314ce',
        nombre: localStorage.getItem('userName') || 'Usuario'
      };

      // Calcular peso total y total de items
      const pesoTotal = itemsConCantidad.reduce((sum, item) => 
        sum + (item.peso * item.cantidad), 0
      );
      const totalItems = itemsConCantidad.reduce((sum, item) => 
        sum + item.cantidad, 0
      );

      // El consecutivo (solicitud_id) lo genera el backend de forma
      // autoincrementable por edificio (ENT-0001, ENT-0002, ...).
      const data = {
        solicitante: usuario,
        alistador: usuario,  // Se marca como alistado inmediatamente
        edificio: {
          id: edificioSeleccionado._id,
          nombre: edificioSeleccionado.nombre,
          ciudad: edificioSeleccionado.ciudad,
          direccion: edificioSeleccionado.direccion
        },
        items: itemsConCantidad.map(item => ({
          id: item.prenda_id,
          nombre: item.nombre,
          cantidad: item.cantidad,
          peso: item.peso,
          tipo: 'lenceria',
          observacion: ''
        })),
        total_items: totalItems,
        peso_total: pesoTotal,
        estado: 'alistado',  // ✅ Cambiado a 'alistado' para seguir el flujo
        fecha_alistamiento: new Date().toISOString(),  // ✅ Fecha de alistamiento
        observaciones: observaciones || ''
      };

      const response = await fetch(`${API_URL}/entregas-lenceria-vertical/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear entrega');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error al crear entrega');
      }

      toast({
        title: 'Éxito',
        description: result.message || 'Entrega creada correctamente',
      });

      // Limpiar formulario y cerrar modal
      handleClose();
      
      // Llamar a onSuccess después de cerrar para recargar datos
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error al crear entrega:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear la entrega',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEdificioSeleccionado(null);
    setPrendasDisponibles([]);
    setItemsSeleccionados([]);
    setObservaciones('');
    setSearchEdificio('');
    onClose();
  };

  const edificiosFiltrados = edificios.filter(edificio =>
    edificio.nombre.toLowerCase().includes(searchEdificio.toLowerCase()) ||
    edificio.ciudad?.toLowerCase().includes(searchEdificio.toLowerCase())
  );

  const totalItems = itemsSeleccionados.reduce((sum, item) => sum + item.cantidad, 0);
  const pesoTotal = itemsSeleccionados.reduce((sum, item) => sum + (item.peso * item.cantidad), 0);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 gap-0">
        <DialogHeader className="p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Package className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
                Nuevo Alistamiento de Lencería
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600 dark:text-gray-400">
                Selecciona el edificio y las prendas a alistar para su entrega
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 p-6">
          {/* Selección de Edificio */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              <Building2 className="h-4 w-4 text-blue-600" />
              Edificio de Destino <span className="text-red-500">*</span>
            </label>

            {!edificioSeleccionado ? (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar edificio por nombre o ciudad..."
                    value={searchEdificio}
                    onChange={(e) => setSearchEdificio(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {isLoadingEdificios ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="border rounded-lg max-h-60 overflow-y-auto">
                    {edificiosFiltrados.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No se encontraron edificios con logística vertical
                      </div>
                    ) : (
                      <div className="divide-y">
                        {edificiosFiltrados.map((edif) => (
                          <div
                            key={edif._id}
                            onClick={() => setEdificioSeleccionado(edif)}
                            className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-gray-900">{edif.nombre}</div>
                                <div className="text-sm text-gray-500">
                                  {edif.ciudad} • {edif.direccion}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {edif.numeroHospedajes} hospedajes
                                  </Badge>
                                  <Badge className="text-xs bg-blue-100 text-blue-700">
                                    {edif.tiposPrendaAsignados?.length || 0} prendas parametrizadas
                                  </Badge>
                                </div>
                              </div>
                              <Building2 className="h-5 w-5 text-blue-600" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{edificioSeleccionado.nombre}</div>
                    <div className="text-sm text-gray-600">
                      {edificioSeleccionado.ciudad} • {edificioSeleccionado.direccion}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEdificioSeleccionado(null);
                      setPrendasDisponibles([]);
                      setItemsSeleccionados([]);
                    }}
                  >
                    Cambiar
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Prendas Disponibles con Cantidad Directa */}
          {edificioSeleccionado && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <Shirt className="h-4 w-4 text-purple-600" />
                  Prendas Disponibles
                  <span className="font-normal text-gray-400">— ingresa cantidades</span>
                </label>
                {itemsSeleccionados.length > 0 && (
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                    {itemsSeleccionados.length} prenda(s) seleccionada(s)
                  </Badge>
                )}
              </div>
              
              {isLoadingPrendas ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                  {prendasDisponibles.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No hay prendas parametrizadas para este edificio
                    </div>
                  ) : (
                    <div className="divide-y">
                      {prendasDisponibles.map((prenda) => {
                        const cantidadActual = getCantidadActual(prenda._id);
                        const tieneSeleccion = cantidadActual > 0;
                        
                        return (
                          <div
                            key={prenda._id}
                            className={`p-4 transition-colors ${
                              tieneSeleccion ? 'bg-blue-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              {/* Información de la prenda */}
                              <div className="flex-1">
                                <div className="font-medium text-sm text-gray-900">{prenda.nombre}</div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-gray-500">{prenda.peso}g por unidad</span>
                                  <Badge 
                                    variant="outline"
                                    className={`text-xs ${
                                      prenda.stock_disponible > 0 
                                        ? 'border-green-300 text-green-700' 
                                        : prenda.stock_disponible === 0
                                        ? 'border-yellow-300 text-yellow-700'
                                        : 'border-red-300 text-red-700'
                                    }`}
                                  >
                                    Stock: {prenda.stock_disponible} {prenda.stock_disponible <= 0 && '(Entrega extra)'}
                                  </Badge>
                                </div>
                              </div>

                              {/* Input de cantidad */}
                              <div className="flex items-center gap-2">
                                <div className="flex flex-col items-end">
                                  <Input
                                    type="number"
                                    min="0"
                                    value={cantidadActual || ''}
                                    onChange={(e) => handleCantidadChange(prenda._id, e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    placeholder="0"
                                    className={`h-10 w-24 text-center font-medium ${
                                      tieneSeleccion ? 'border-blue-500 bg-white' : ''
                                    }`}
                                  />
                                  {tieneSeleccion && (
                                    <span className="text-xs text-blue-600 mt-1">
                                      {(prenda.peso * cantidadActual / 1000).toFixed(2)} kg
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Resumen */}
              {itemsSeleccionados.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Items</span>
                    <span className="text-xl font-bold text-blue-600">{totalItems}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Peso Total</span>
                    <span className="text-xl font-bold text-purple-600">
                      {(pesoTotal / 1000).toFixed(2)} kg
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Observaciones */}
          {edificioSeleccionado && (
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Observaciones <span className="font-normal text-gray-400">(opcional)</span>
              </label>
              <Textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Agregar observaciones sobre la entrega..."
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter className="p-6 border-t bg-gray-50 dark:bg-gray-900">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleCrearEntrega}
            disabled={isLoading || !edificioSeleccionado || itemsSeleccionados.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creando...
              </>
            ) : (
              <>
                <Package className="h-4 w-4 mr-2" />
                Crear Alistamiento
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CrearEntregaDirectaModal;
